import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import loteService, { LoteNumeracion } from '../../services/loteService';
import { getApiErrorMessage } from '../../utils/apiError';
import { searchableSelectAddButtonSx } from '../SearchableSelect';

export interface ProductoLoteControlFields {
  controlLoteSerie?: string | null;
  requiereVencimiento?: boolean | null;
  diasAlertaVencimiento?: number | null;
  idLoteNumeracion?: number | null;
  invetariado?: number | null;
  tipoArticulo?: string | null;
  cantidad?: number | null;
}

interface ProductoFormLoteControlsProps {
  formData: ProductoLoteControlFields;
  onChange: (patch: Partial<ProductoLoteControlFields>) => void;
  inventariadoEditable: boolean;
}

const ProductoFormLoteControls: React.FC<ProductoFormLoteControlsProps> = ({
  formData,
  onChange,
  inventariadoEditable,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const control = (formData.controlLoteSerie || 'N').toUpperCase();
  const esSer = String(formData.tipoArticulo ?? '').trim().toUpperCase() === 'SER';
  const inventariado = !!formData.invetariado;
  const disabled = !inventariado || esSer || !inventariadoEditable;
  const stockCero = (formData.cantidad ?? 0) === 0;
  const controlActivo = control === 'L' || control === 'S';

  const [numeraciones, setNumeraciones] = useState<LoteNumeracion[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nueva, setNueva] = useState({
    nombre: '',
    mascara: 'LOT-{YYYY}{MM}-{SEQ:4}',
  });

  const reloadNumeraciones = useCallback(async () => {
    try {
      const list = await loteService.listNumeracion();
      setNumeraciones(list.filter((n) => n.activo !== false));
    } catch {
      setNumeraciones([]);
    }
  }, []);

  useEffect(() => {
    if (!controlActivo) return;
    void reloadNumeraciones();
  }, [controlActivo, reloadNumeraciones]);

  const handleCrearMascara = async () => {
    const nombre = nueva.nombre.trim();
    const mascara = nueva.mascara.trim();
    if (!nombre || !mascara) {
      enqueueSnackbar('Nombre y máscara son obligatorios', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const created = await loteService.createNumeracion({
        nombre,
        mascara,
        ambito: 'G',
        codigoArticulo: null,
      });
      await reloadNumeraciones();
      onChange({ idLoteNumeracion: created.idLoteNumeracion });
      setDialogOpen(false);
      setNueva({ nombre: '', mascara: 'LOT-{YYYY}{MM}-{SEQ:4}' });
      enqueueSnackbar('Máscara creada', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo crear la máscara'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
          Control de lote / serie
        </Typography>
        {!stockCero && control === 'N' && (
          <Typography variant="caption" color="warning.main">
            Para activar el control, el stock del producto debe estar en cero.
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControl fullWidth size="small" disabled={disabled || (!stockCero && control === 'N')}>
          <InputLabel>Control</InputLabel>
          <Select
            label="Control"
            value={control}
            onChange={(e) => {
              const next = e.target.value;
              onChange({
                controlLoteSerie: next,
                ...(next === 'N' ? { idLoteNumeracion: null } : {}),
              });
            }}
          >
            <MenuItem value="N">Sin control</MenuItem>
            <MenuItem value="L">Por lote</MenuItem>
            <MenuItem value="S">Por serie</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
          <FormControlLabel
            disabled={disabled || control === 'N'}
            control={
              <Switch
                checked={!!formData.requiereVencimiento}
                onChange={(e) => onChange({ requiereVencimiento: e.target.checked })}
                color="primary"
              />
            }
            label="Requiere vencimiento"
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Días alerta vencimiento"
          value={formData.diasAlertaVencimiento ?? 30}
          onChange={(e) => onChange({ diasAlertaVencimiento: Number(e.target.value) || 0 })}
          disabled={disabled || control === 'N'}
          inputProps={{ min: 0 }}
        />
      </Grid>
      {controlActivo && (
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Máscara de numeración</InputLabel>
            <Select
              label="Máscara de numeración"
              value={formData.idLoteNumeracion ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                onChange({ idLoteNumeracion: v === '' ? null : Number(v) });
              }}
              endAdornment={
                <InputAdornment position="end" sx={{ mr: 2 }}>
                  <Tooltip title="Crear máscara">
                    <span>
                      <IconButton
                        type="button"
                        size="small"
                        disabled={disabled}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogOpen(true);
                        }}
                        aria-label="Crear máscara"
                        sx={searchableSelectAddButtonSx}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              }
            >
              <MenuItem value="">
                <em>Sin máscara preferida</em>
              </MenuItem>
              {numeraciones.map((n) => (
                <MenuItem key={n.idLoteNumeracion} value={n.idLoteNumeracion}>
                  {n.nombre} ({n.mascara})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nueva máscara</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              required
              fullWidth
              size="small"
              value={nueva.nombre}
              onChange={(e) => setNueva((p) => ({ ...p, nombre: e.target.value }))}
              autoFocus
            />
            <TextField
              label="Máscara"
              required
              fullWidth
              size="small"
              value={nueva.mascara}
              onChange={(e) => setNueva((p) => ({ ...p, mascara: e.target.value }))}
              helperText="Ej. LOT-{YYYY}{MM}-{SEQ:4}"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCrearMascara} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProductoFormLoteControls;
